import {useCallback, useEffect, useState} from "react"

import type {UnidentifiedItemResponse} from "./types"

import {api} from "@indebted/api"

type UnidentifiedItemState = {
	data: UnidentifiedItemResponse | null
	loading: boolean
	error: string
	notFound: boolean
}

type UnidentifiedInboxItem = UnidentifiedItemState & {
	refetch: () => void
}

const FAILED_TO_LOAD = "Failed to load the message"

function itemURL(channel: string, conversationID: string, providerMessageID: string, agentEmail: string): string {
	const path = api.inbox.unidentifiedItemPath(channel, conversationID, providerMessageID)

	return api.url(`${path}?AgentEmail=${encodeURIComponent(agentEmail)}`)
}

// Spelled out here rather than added to @indebted/api because req() drops
// res.status, and this page distinguishes a missing message from a failed read.
// Resolves every outcome and rejects none, so there is no way to leave the page
// loading.
async function readItem(url: string): Promise<Omit<UnidentifiedItemState, "loading">> {
	let response: Response
	try {
		response = await fetch(url, {method: "GET"})
	} catch {
		return {data: null, error: FAILED_TO_LOAD, notFound: false}
	}

	if (response.status === 404) return {data: null, error: "", notFound: true}

	const body = await response.json().catch(() => null)
	if (!response.ok || body === null) {
		return {data: null, error: failureMessage(body), notFound: false}
	}

	return {data: body as UnidentifiedItemResponse, error: "", notFound: false}
}

// Failure bodies only: Message here is the endpoint's error string, not the
// message under review.
function failureMessage(body: unknown): string {
	if (typeof body === "object" && body !== null) {
		const message = (body as {Message?: unknown}).Message
		if (typeof message === "string" && message !== "") return message
	}

	return FAILED_TO_LOAD
}

function useUnidentifiedInbox(
	channel: string,
	conversationID: string,
	providerMessageID: string,
	agentEmail?: string,
): UnidentifiedInboxItem {
	const [state, setState] = useState<UnidentifiedItemState>({
		data: null,
		loading: true,
		error: "",
		notFound: false,
	})
	const [reloadToken, setReloadToken] = useState(0)

	useEffect(() => {
		let active = true
		setState({data: null, loading: true, error: "", notFound: false})
		// The endpoint gates UnidentifiedInboxEnabled on the agent, so reading
		// before Okta resolves one would flash "unavailable" at an entitled agent.
		if (!agentEmail) return

		readItem(itemURL(channel, conversationID, providerMessageID, agentEmail)).then((result) => {
			if (active) setState({...result, loading: false})
		})
		return () => {
			active = false
		}
	}, [channel, conversationID, providerMessageID, agentEmail, reloadToken])

	const refetch = useCallback(() => setReloadToken((token) => token + 1), [])

	return {...state, refetch}
}

export {useUnidentifiedInbox}
export type {UnidentifiedInboxItem}
